import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, computed, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import { AutoCostReport } from '../../model/autocost/auto-cost-report';
import { AutoCostLine } from '../../model/autocost/auto-cost-line';
import { SkuCostService } from '../../services/sku-cost.service';
import { Router } from '@angular/router';
import { StringUtils } from '../../utils/string-utils';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-auto-cost-report',
  templateUrl: './auto-cost-report.component.html',
  styleUrls: ['./auto-cost-report.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    MatIcon,
    MatProgressSpinner,
    MatTooltip,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
  ],
})
export class AutoCostReportComponent implements OnInit, OnChanges {
  private readonly service = inject(SkuCostService);
  private readonly router = inject(Router);
  readonly report = signal<AutoCostReport>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly entityId = input.required<string>();
  readonly entityRevision = input.required<string>();
  readonly revision = computed(() => +this.entityRevision());

  private static readonly MAX_PRN_LENGTH = 70;

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.getReport();
    }
  }

  ngOnInit(): void {
    if (!this.entityId() || StringUtils.isEmptyNumericValue(this.revision())) {
      this.router.navigate(['/']).then(() => this.isBusy.set(false));
    } else {
      this.getReport();
    }
  }

  backToEntity(): void {
    if (!this.entityId() || StringUtils.isEmptyNumericValue(this.revision())) {
      this.router.navigate(['/']).then(() => this.isBusy.set(false));
    } else {
      const entityName: string = this.entityId().startsWith('QT') ? 'quotes' : 'orders';
      this.router.navigate([entityName, this.entityId(), this.revision()]).then(() => this.isBusy.set(false));
    }
  }

  getReport(): void {
    this.report.set(undefined!);
    this.errorMessage.set(undefined!);
    if (this.entityId()?.trim()?.length) {
      this.isBusy.set(true);
      this.service.getAutoCostReport(this.entityId(), this.revision()).subscribe({
        next: (report: AutoCostReport) => {
          if (report?.lines?.length) {
            report.lines = report.lines.sort((first, second) => +first.lineId - +second.lineId);
          }
          this.report.set(report);
        },
        error: (error: HttpErrorResponse) => this.handleError(error),
        complete: () => this.isBusy.set(false),
      });
    }
  }

  truncatePrn(prn: string): string {
    const text: string = prn || '';
    return text.length > AutoCostReportComponent.MAX_PRN_LENGTH ? text.substring(0, AutoCostReportComponent.MAX_PRN_LENGTH - 1) + '…' : text;
  }

  extendedEstimate(line: AutoCostLine): number {
    return (line.estimate || 0) * (line.quantity || 0);
  }

  isEngineered(line: AutoCostLine): boolean {
    return line.source === 'ENGINEERED';
  }

  costToPricePercent(line: AutoCostLine): number | null {
    return line.quotedUnitPrice ? (line.estimate / line.quotedUnitPrice) * 100 : null;
  }

  humanise(reason: string | null): string {
    const text: string = (reason || '').replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  sfrTooltip(line: AutoCostLine): string {
    return (line.sfrs || []).map((sfr) => sfr.sfrNumber + ' ' + (sfr.description || '') + (sfr.seen ? '' : ' (never built)')).join('\n');
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }
}
